import { DOCUMENT, inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, Subject, throwError, of, switchMap } from 'rxjs';
import { Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Company, User, UserProfile } from '../models/context.model';
import { ContextService } from '../services/context.service';
import { AppService } from '../../../app.service';
import { URLS_GUARD } from '../models/url-guard.enum';
import { UserType } from '../../types/user-type.enum';

@Injectable({
	providedIn: 'root'
})
export class AuthService implements OnDestroy {
	private user = new BehaviorSubject<User>(null as any);

	readonly #appService = inject(AppService);
	readonly #router = inject(Router);
	readonly #contextService = inject(ContextService);
	readonly #httpClient = inject(HttpClient);
	readonly #document = inject(DOCUMENT);

	get getUserLogged(): Observable<User> {
		return this.user.asObservable();
	}

	setUserLogged(user: any): void {
		this.user.next(user);
	}

	get userLoggedValue(): User {
		return this.user.value;
	}

	private readonly _destroying$ = new Subject<void>();

	// Ultimo stato della sessione accounts osservato su un fallimento del token:
	// guida la scelta dell'ingresso in login(). Vedi noteAccountsSession().
	#accountsSessionAlive = false;

	//token
	private token: BehaviorSubject<any> = new BehaviorSubject(null);

	setToken(_token: any) {
		this.token.next(_token);
	}

	get getTokenValue() {
		return this.token?.value ?? undefined;
	}

	get getTokenObservable() {
		return this.token.asObservable();
	}

	authentication(state: RouterStateSnapshot, companyId?: number) {
		return this.getJwtToken(companyId).pipe(
			switchMap((initialRes: any) => {
				const tokenInfo = this.getDecodedAccessToken(initialRes.JwtToken);
				const user = this.userMapper(tokenInfo);

				if (user.UserType?.toLowerCase() === UserType.PHOTOGRAPHER && (!user.Roles || user.Roles.length === 0)) {
					// Set the token temporarily for the syncUser call to be authenticated
					this.setToken(initialRes.JwtToken);
					// After syncing, get the new token and pass its response to the next operator
					// return this.settingsService.syncUser(Number(user.Id!)).pipe(switchMap(() => this.getJwtToken()));
				}

				// Senza ruoli AuthorizationGuard manda a /error/forbidden e AppComponent
				// rilancia auth troppo tardi. Se c'è COMPANY_SELECTED, prendi subito il JWT con companyId.
				if ((!user.Roles || user.Roles.length === 0) && companyId == null) {
					const companySelected = document.cookie.split(';').find((cookie) => cookie.trim().startsWith('COMPANY_SELECTED='));
					if (companySelected) {
						const selectedCompanyId = Number(companySelected.split('=')[1]);
						if (!Number.isNaN(selectedCompanyId)) {
							return this.getJwtToken(selectedCompanyId);
						}
					}
				}

				return of(initialRes);
			}),
			map((finalRes: any) => {
				// This is now the single point of truth for setting the token and context.
				// It uses the final token, whether it's the original or the refreshed one.
				this.setToken(finalRes.JwtToken);
				let tokenInfo = this.getDecodedAccessToken();
				this.mapContext(tokenInfo);

				this.setUserLogged(this.#contextService.context.Current.User);

				// recupero la rotta richiesta dalla sessione
				let route = state.url;

				// AppComponent può rilanciare authentication con uno snapshot ancora vuoto
				if (!route || route === '/') {
					const loc = this.#document.defaultView?.location;
					route = loc ? `${loc.pathname}${loc.search}${loc.hash}` : route;
				}

				if (route === URLS_GUARD.AUTH_URL || route?.startsWith('/error')) {
					route = localStorage.getItem('BARequestRoute') ?? route;
					// non rimuovere BARequestRoute: AuthGuard.handleAuthenticatedUser lo rilegge dopo
				} else if (route && route !== '/' && !this.isEntryRoute(route)) {
					localStorage.setItem('BARequestRoute', route);
				}

				return this.parseUrl(route);
			}),
			catchError((err: HttpErrorResponse) => {
				console.error(err);
				let currentPage = state.url;
				if (!currentPage || currentPage === '/') {
					const loc = this.#document.defaultView?.location;
					currentPage = loc ? `${loc.pathname}${loc.search}${loc.hash}` : currentPage;
				}
				if (currentPage !== URLS_GUARD.AUTH_URL && !currentPage?.startsWith('/error')) {
					localStorage.setItem('BARequestRoute', currentPage);
					this.login(this.noteAccountsSession(err));
					return throwError(() => false);
				}
				this.handleHttpError(err.status);
				return throwError(() => false);
			})
		);
	}

	/**
	 * Scrive COMPANY_SELECTED con la **stessa scadenza del JWT**: la company scelta resta valida
	 * quanto il token che la porta, e sopravvive alla chiusura del browser. Senza `exp` leggibile
	 * si ricade sul cookie di sessione, che è il comportamento storico.
	 */
	setCompanySelected(companyId: number): void {
		const exp = this.getDecodedAccessToken()?.exp;
		const expires = exp ? `; expires=${new Date(exp * 1000).toUTCString()}` : '';
		const secure = location.protocol === 'https:' ? '; secure' : '';

		document.cookie = `COMPANY_SELECTED=${companyId}; path=/; SameSite=Lax${expires}${secure}`;
	}

	/** Rimuove COMPANY_SELECTED quando la company scelta non vale più (cambio profilo, logoff, zero ruoli). */
	clearCompanySelected(): void {
		document.cookie = 'COMPANY_SELECTED=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
	}

	/**
	 * Login e company-selection sono rotte di **ingresso**: ci si passa, non ci si torna. Non vanno
	 * salvate in `BARequestRoute`, altrimenti dopo l'autenticazione si viene rimandati lì invece che
	 * sulla pagina richiesta — lo leggono `authentication()`, `AuthGuard.handleAuthenticatedUser` e
	 * la pagina di login.
	 */
	isEntryRoute(route: string): boolean {
		return route.includes(URLS_GUARD.LOGIN) || route.includes(URLS_GUARD.COMPANY_SELECTION);
	}

	isPhotographer() {
		return this.userLoggedValue?.UserType == UserType.PHOTOGRAPHER;
	}

	isCompany() {
		return this.userLoggedValue?.UserType == UserType.COMPANY;
	}

	getLicenseeCompanyId() {
		let profile = this.userLoggedValue.UserProfiles?.filter((p) => p.Id == this.userLoggedValue.CurrentProfileId);
		if (profile?.length) {
			return profile[0].Company?.Id;
		}
		return false;
	}

	handleHttpError(status: number) {
		switch (status) {
			case 401: {
				this.#router.navigateByUrl('error/unauthorized');
				break;
			}
			case 403:
				this.#router.navigateByUrl('error/forbidden');
				break;
			default:
				this.#router.navigateByUrl('error/server-error');
		}
	}

	ngOnDestroy(): void {
		this._destroying$.next(undefined);
		this._destroying$.complete();
	}

	/**
	 * Manda su accounts per autenticarsi e tornare su /auth.
	 *
	 * Con `sessionAlive` entriamo direttamente dalla scelta profilo: se la sessione accounts è già
	 * autenticata, `/account/login` salta il form e redirige su `/Account/pick-profile` **senza
	 * propagare il returnUrl**, e alla scelta del profilo si finisce sulla LandingUrl del profilo
	 * (my.basic.net) invece che su Studios. `pick-profile` invece il returnUrl lo onora.
	 * Vedi docs/Auth-ReturnUrl-PickProfile.md — workaround, il fix vero è lato accounts.
	 */
	login(sessionAlive = this.#accountsSessionAlive) {
		const settings = this.#contextService?.context?.Settings;
		const returnUrl = encodeURIComponent(settings?.AppPaths?.Local?.Base + '/auth');
		const entryUrl = sessionAlive && settings.PickProfileUrl ? settings.PickProfileUrl : settings.LoginUrl;

		location.href = entryUrl + '?returnUrl=' + returnUrl;
	}

	/**
	 * Registra e restituisce lo stato della sessione accounts osservato su un fallimento di
	 * `GetBasicStudiosJwtToken`, per scegliere l'ingresso in `login()`.
	 *
	 * Senza sessione l'endpoint risponde 401 con la pagina IIS "You do not have permission to view
	 * this directory or page.": quella firma la trattiamo come sessione assente. Un 403, o un 401
	 * con un corpo diverso, lo trattiamo come sessione viva senza profilo valido per Studios.
	 * Nel dubbio si resta sul comportamento storico (login classico), che per la sessione assente
	 * funziona: il workaround non può peggiorare il caso che oggi va.
	 */
	noteAccountsSession(err: HttpErrorResponse): boolean {
		if (err.status === 0) {
			this.#accountsSessionAlive = false; // rete o CORS: non sappiamo nulla
		} else if (err.status === 401) {
			const body = typeof err.error === 'string' ? err.error : (err.error?.text ?? '');
			this.#accountsSessionAlive = !/do not have permission/i.test(body);
		} else {
			this.#accountsSessionAlive = err.status === 403;
		}

		return this.#accountsSessionAlive;
	}

	logout() {
		this.#httpClient
			.post(this.#contextService.context.Settings.LogoffUrl + '?returnUrl=' + this.#contextService.context.Settings.LoginUrl, {}, { withCredentials: true })
			.subscribe(() => {
				this.setToken(undefined);
				this.setUserLogged(null);
				// dopo il logoff la sessione accounts non c'è più: il prossimo login riparte dal form
				this.#accountsSessionAlive = false;
				// il cookie ora sopravvive al browser: senza questa pulizia la company resterebbe scelta
				this.clearCompanySelected();
				//con il logoff deve eliminarsi anche il redirect post login per poter tornare in home (dashboard)
				localStorage.clear();
				this.login();
			});
	}

	getDecodedAccessToken(token?: string): any {
		if (token) {
			try {
				return jwtDecode(token);
			} catch (Error) {
				return null;
			}
		}
		if (this.token.value) {
			try {
				return jwtDecode(this.token.value);
			} catch (Error) {
				return null;
			}
		}
	}

	userMapper(inputObject: any): User {
		const userObject: User = {
			Id: inputObject.Id,
			FirstName: inputObject.FirstName,
			LastName: inputObject.LastName,
			CurrentProfileId: inputObject.CurrentProfileId,
			ClientId: inputObject.ClientId,
			UserType: inputObject.UserType,
			ImgPath: inputObject.ImgPath,
			UserName: inputObject.UserName,
			UserProfiles: [],
			Roles: []
		};

		if (inputObject.Roles && inputObject.UserProfiles) return inputObject;

		// Iterate through indices and map UserProfiles
		let index = 0;
		while (inputObject[`UserProfiles[${index}].Id`] !== undefined) {
			const company: Company = {
				GroupId: inputObject[`UserProfiles[${index}].Company.GroupId`],
				Id: inputObject[`UserProfiles[${index}].CompanyId`]
			};
			const userProfile: UserProfile = {
				Id: inputObject[`UserProfiles[${index}].Id`],
				PlatformId: inputObject[`UserProfiles[${index}].PlatformId`],
				ProfileName: inputObject[`UserProfiles[${index}].ProfileName`],
				IsCustom: inputObject[`UserProfiles[${index}].IsCustom`] === 'True' ? true : false,
				Company: company
			};
			userObject.UserProfiles?.push(userProfile);
			index++;
		}

		index = 0;
		while (inputObject[`Roles[${index}]`] !== undefined) {
			userObject.Roles?.push(inputObject[`Roles[${index}]`]);
			index++;
		}

		const activeProfile = userObject.UserProfiles?.find(el => el.Id === userObject.CurrentProfileId);
		this.#appService.user = { userId: +userObject.Id!, userFirstName: userObject.FirstName!, userLastName: userObject.LastName! };
		this.#appService.companyId = +inputObject['BrowseCompanyId'];
		this.#appService.userType = userObject.UserType!;

		return userObject;
	}

	getJwtToken(companyId?: number) {
		this.#appService.companyId = companyId ?? undefined;
		let jwtPage = this.#contextService?.context?.Settings?.AppPaths?.Core?.Api + '/account/GetBasicStudiosJwtToken';
		jwtPage = companyId ? jwtPage + '?companyId=' + companyId : jwtPage;
		return this.#httpClient.get(jwtPage, { withCredentials: true });
	}

	mapContext(pageInfo: any) {
		this.#contextService.context.Current.User = this.userMapper(pageInfo);
		this.#contextService.setObservableContext(this.#contextService.context);
	}

	parseUrl(url: string): UrlTree {
		return this.#router.parseUrl(url);
	}

	redirectToUnauthorized() {
		return this.parseUrl('error/unauthorized');
	}

	redirectToForbidden() {
		return this.parseUrl('error/forbidden');
	}

	redirectToHome() {
		return this.parseUrl('/');
	}

	redirectToNotFound() {
		return this.parseUrl('error/not-found');
	}
}
